// src/components/NotepadPanel.tsx
import React, { useEffect, useState } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import TextAlign from '@tiptap/extension-text-align';
import TaskList from '@tiptap/extension-task-list';
import TaskItem from '@tiptap/extension-task-item';
import HorizontalRule from '@tiptap/extension-horizontal-rule';
import TextStyle from '@tiptap/extension-text-style';
import Color from '@tiptap/extension-color';
import Placeholder from '@tiptap/extension-placeholder';
import { ColorPicker } from '@mantine/core';
import {
  TbBold,
  TbItalic,
  TbStrikethrough,
  TbH1,
  TbH2,
  TbH3,
  TbList,
  TbListNumbers,
  TbAlignLeft,
  TbAlignCenter,
  TbAlignRight,
  TbCheckbox,
  TbSeparator,
  TbPalette,
  TbX,
} from 'react-icons/tb';
import './NotepadPanel.css';

interface NotepadPanelProps {
  content: string;
  onContentChange: (content: string) => void;
  onClose: () => void;
  isModal?: boolean;
  width?: number;
  onWidthChange?: (width: number) => void;
}

export const NotepadPanel: React.FC<NotepadPanelProps> = ({
  content,
  onContentChange,
  onClose,
  isModal = false,
  width = 305,
  onWidthChange,
}) => {
  const [isColorPickerOpen, setIsColorPickerOpen] = useState(false);
  const [selectedColor, setSelectedColor] = useState('#000000');
  const [isResizing, setIsResizing] = useState(false);

  const editor = useEditor({
    extensions: [
      StarterKit,
      Link.configure({
        openOnClick: true,
        HTMLAttributes: {
          class: 'editor-link',
        },
      }),
      TextAlign.configure({
        types: ['heading', 'paragraph'],
      }),
      TaskList,
      TaskItem.configure({
        nested: true,
      }),
      HorizontalRule,
      TextStyle,
      Color,
      Placeholder.configure({
        placeholder: 'Jot notes here before they become tasks...',
      }),
    ],
    content: content ? JSON.parse(content) : '',
    onUpdate: ({ editor }) => {
      const json = JSON.stringify(editor.getJSON());
      onContentChange(json);
    },
  });

  // Handle click outside to close color picker
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.color-picker-container')) {
        setIsColorPickerOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Update selected color when text style changes
  useEffect(() => {
    if (editor) {
      const currentColor = editor.getAttributes('textStyle').color;
      if (currentColor) {
        setSelectedColor(currentColor);
      }
    }
  }, [editor]);

  // Update editor content when prop changes (e.g., after loading from Firestore)
  useEffect(() => {
    if (editor && content) {
      const currentContent = JSON.stringify(editor.getJSON());
      if (currentContent !== content) {
        editor.commands.setContent(JSON.parse(content));
      }
    }
  }, [content, editor]);

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  // Handle resize
  useEffect(() => {
    if (!isResizing || isModal) return;

    const handleMouseMove = (e: MouseEvent) => {
      const newWidth = e.clientX - 64; // 64px is sidebar width
      if (onWidthChange) {
        onWidthChange(newWidth);
      }
    };

    const handleMouseUp = () => {
      setIsResizing(false);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing, isModal, onWidthChange]);

  const panelContent = (
    <div
      className={`notepad-panel ${isResizing ? 'resizing' : ''}`}
      style={!isModal ? { width: `${width}px` } : undefined}
    >
      {!isModal && (
        <div
          className="panel-resize-handle"
          onMouseDown={() => setIsResizing(true)}
        />
      )}
      <div className="notepad-header">
        <span className="notepad-title">Notepad</span>
        <button className="notepad-close" onClick={onClose} aria-label="Close notepad">
          <TbX />
        </button>
      </div>
      <div className="notepad-editor">
        <div className="editor-toolbar">
          <button
            onClick={() => editor?.chain().focus().toggleTaskList().run()}
            className={editor?.isActive('taskList') ? 'is-active' : ''}
            title="Task List"
          >
            <TbCheckbox />
          </button>
          <button
            onClick={() => editor?.chain().focus().toggleBulletList().run()}
            className={editor?.isActive('bulletList') ? 'is-active' : ''}
            title="Bullet List"
          >
            <TbList />
          </button>
          <button
            onClick={() => editor?.chain().focus().toggleOrderedList().run()}
            className={editor?.isActive('orderedList') ? 'is-active' : ''}
            title="Numbered List"
          >
            <TbListNumbers />
          </button>
          <div className="toolbar-separator" />
          <button
            onClick={() => editor?.chain().focus().setHorizontalRule().run()}
            title="Horizontal Rule"
          >
            <TbSeparator />
          </button>
          <div className="toolbar-separator" />
          <button
            onClick={() => editor?.chain().focus().toggleBold().run()}
            className={editor?.isActive('bold') ? 'is-active' : ''}
            title="Bold"
          >
            <TbBold />
          </button>
          <button
            onClick={() => editor?.chain().focus().toggleItalic().run()}
            className={editor?.isActive('italic') ? 'is-active' : ''}
            title="Italic"
          >
            <TbItalic />
          </button>
          <button
            onClick={() => editor?.chain().focus().toggleStrike().run()}
            className={editor?.isActive('strike') ? 'is-active' : ''}
            title="Strikethrough"
          >
            <TbStrikethrough />
          </button>
          <div className="toolbar-separator" />
          <button
            onClick={() =>
              editor?.chain().focus().toggleHeading({ level: 1 }).run()
            }
            className={
              editor?.isActive('heading', { level: 1 }) ? 'is-active' : ''
            }
            title="Heading 1"
          >
            <TbH1 />
          </button>
          <button
            onClick={() =>
              editor?.chain().focus().toggleHeading({ level: 2 }).run()
            }
            className={
              editor?.isActive('heading', { level: 2 }) ? 'is-active' : ''
            }
            title="Heading 2"
          >
            <TbH2 />
          </button>
          <button
            onClick={() =>
              editor?.chain().focus().toggleHeading({ level: 3 }).run()
            }
            className={
              editor?.isActive('heading', { level: 3 }) ? 'is-active' : ''
            }
            title="Heading 3"
          >
            <TbH3 />
          </button>
          <div className="toolbar-separator" />
          <button
            onClick={() => editor?.chain().focus().setTextAlign('left').run()}
            className={
              editor?.isActive({ textAlign: 'left' }) ? 'is-active' : ''
            }
            title="Align Left"
          >
            <TbAlignLeft />
          </button>
          <button
            onClick={() =>
              editor?.chain().focus().setTextAlign('center').run()
            }
            className={
              editor?.isActive({ textAlign: 'center' }) ? 'is-active' : ''
            }
            title="Align Center"
          >
            <TbAlignCenter />
          </button>
          <button
            onClick={() =>
              editor?.chain().focus().setTextAlign('right').run()
            }
            className={
              editor?.isActive({ textAlign: 'right' }) ? 'is-active' : ''
            }
            title="Align Right"
          >
            <TbAlignRight />
          </button>
          <div className="toolbar-separator" />
          <div className="color-picker-container">
            <button
              onClick={() => setIsColorPickerOpen(!isColorPickerOpen)}
              className={editor?.isActive('textStyle') ? 'is-active' : ''}
              title="Text Color"
              style={{ color: selectedColor }}
            >
              <TbPalette />
            </button>
            {isColorPickerOpen && (
              <div className="color-picker-dropdown">
                <ColorPicker
                  format="hex"
                  value={selectedColor}
                  onChange={color => {
                    setSelectedColor(color);
                    editor?.chain().focus().setColor(color).run();
                  }}
                  swatches={[
                    '#000000',
                    '#25262b',
                    '#868e96',
                    '#fa5252',
                    '#e64980',
                    '#be4bdb',
                    '#7950f2',
                    '#4c6ef5',
                    '#228be6',
                    '#15aabf',
                    '#12b886',
                    '#40c057',
                    '#82c91e',
                    '#fab005',
                    '#fd7e14',
                  ]}
                />
              </div>
            )}
          </div>
        </div>
        <EditorContent editor={editor} />
      </div>
    </div>
  );

  if (isModal) {
    return (
      <div className="notepad-modal" onClick={handleOverlayClick}>
        {panelContent}
      </div>
    );
  }

  return panelContent;
};
